import { AssetGroup } from '@play-co/timestep-core';
import View from '@play-co/timestep-core/lib/ui/View';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import { downscale } from 'src/conf/scale';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import { GemsWallet } from 'src/game/components/GemsWallet';
import { Feature } from 'src/replicant/analytics';
import i18n from 'src/lib/i18n/i18n';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import sounds from 'src/lib/sounds';
import { movesPinchCheck } from 'src/lib/pinch';
import buttons from 'src/lib/ui/config/buttons';

const optionPanelWidth = 270;
const optionPanelHeight = 280;
const optionPanelY = 230;
const optionCtaY = 390;
const optionMessageColor = '#BF8147';
const optionHighlightColor = '#EF5E5E';
const optionTopMessageColor = '#B48A52';

type OpenArgs = {
  amount: number;
  continues: number;
  price: number;
  rewardMoves: number;
};

type CloseHandler = (result: {
  purchase?: boolean;
  acceptTos?: boolean;
}) => void;

export default class PopupTosPuzzlePinchNudge extends PopupBasic {
  private contentWrapper: View;
  private closeHandler: CloseHandler;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close({}),
      zIndex: 10000,
      width: 630,
      height: 520,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PUZZLE_PINCH_TOS_NUDGE._,
      },
    });

    this.closeHandler = opts.close;

    this.title.updateOpts({ size: 30 });
    this.message.removeFromSuperview();

    new GemsWallet({
      superview: this.root,
      tracking: { feature: 'header', $subFeature: 'buy-gems' },
    });

    this.contentWrapper = new View({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/hud/icons/hud_gem.png',
      'assets/ui/match2/common/panel_pinch.png',
      'assets/ui/tosPuzzlePinch/continue_gems.png',
      'assets/ui/tosPuzzlePinch/continue_tos.png',
    ]);
  }

  public init(args: OpenArgs) {
    super.init({
      title: i18n('match2.tosContinue.title'),
    });

    this.createOptionPanel({
      x: 170,
      icon: 'assets/ui/tosPuzzlePinch/continue_gems.png',
      iconWidth: downscale(274),
      iconHeight: downscale(265),
      iconY: 105,
      messageY: 230,
      rewardMoves: args.rewardMoves,
    });

    this.createOptionPanel({
      x: 460,
      icon: 'assets/ui/tosPuzzlePinch/continue_tos.png',
      iconWidth: downscale(319),
      iconHeight: downscale(278),
      iconY: 125,
      messageY: 230,
      rewardMoves: args.rewardMoves,
      topMessage: i18n('match2.tosContinue.tosMessage'),
    });

    this.createBuyButton(args.price);
    this.createTosButton();
  }

  private createOptionPanel(args: {
    x: number;
    icon: string;
    iconWidth: number;
    iconHeight: number;
    iconY: number;
    messageY: number;
    rewardMoves: number;
    topMessage?: string;
  }) {
    const panel = new ImageScaleView({
      superview: this.contentWrapper,
      image: 'assets/ui/match2/common/panel_pinch.png',
      width: optionPanelWidth,
      height: optionPanelHeight,
      centerOnOrigin: true,
      centerAnchor: true,
      x: args.x,
      y: optionPanelY,
    });

    if (args.topMessage) {
      new LangBitmapFontTextView({
        superview: panel,
        font: 'Body',
        color: optionTopMessageColor,
        size: 23,
        width: panel.style.width - 24,
        height: 40,
        x: panel.style.width * 0.5,
        y: 28,
        centerOnOrigin: true,
        centerAnchor: true,
        align: 'center',
        verticalAlign: 'center',
        localeText: () => args.topMessage || '',
      });
    }

    new ImageScaleView({
      superview: panel,
      image: args.icon,
      width: args.iconWidth,
      height: args.iconHeight,
      scaleMethod: 'contain',
      x: panel.style.width * 0.5,
      y: args.iconY,
      centerOnOrigin: true,
      centerAnchor: true,
    });

    new LangBitmapFontTextView({
      superview: panel,
      font: 'Body',
      color: optionMessageColor,
      size: 26,
      width: panel.style.width - 34,
      height: 76,
      x: panel.style.width * 0.5,
      y: args.messageY,
      centerOnOrigin: true,
      centerAnchor: true,
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
      isRichText: true,
      leading: -8,
      localeText: () =>
        i18n('match2.tosContinue.buyMessage', {
          amount:
            '[color=' +
            optionHighlightColor +
            ']' +
            args.rewardMoves +
            '[/color]',
        }),
    });
  }

  private createBuyButton(price: number) {
    const button = new ButtonScaleViewWithText({
      ...buttons.secondary,
      superview: this.contentWrapper,
      x: 35,
      y: optionCtaY,
      width: 270,
      height: 90,
      font: 'Body',
      fontSize: 38,
      labelWidth: 130,
      labelOffsetX: 0,
      localeText: () => i18n('match2.tosContinue.playOn'),
      onClick: async () => {
        sounds.sfx.play('ok');
        const success = await movesPinchCheck(price, 'match2');
        if (success) {
          this.closeHandler({ purchase: true });
        }
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_OK,
      },
    });

    new ImageScaleView({
      superview: button,
      image: 'assets/ui/hud/icons/hud_gem.png',
      width: 47,
      height: 40,
      scaleMethod: 'contain',
      x: 150,
      y: button.style.height * 0.5 - 2,
      centerOnOrigin: true,
      centerAnchor: true,
    });

    new LangBitmapFontTextView({
      superview: button,
      font: 'Body',
      color: '#FFFFFF',
      size: 34,
      width: 60,
      height: 40,
      x: 210,
      y: button.style.height * 0.5 - 4,
      centerOnOrigin: true,
      centerAnchor: true,
      align: 'left',
      verticalAlign: 'center',
      localeText: () => String(price),
    });
  }

  private createTosButton() {
    new ButtonScaleViewWithText({
      ...buttons.blue,
      superview: this.contentWrapper,
      x: 325,
      y: optionCtaY,
      width: 270,
      height: 90,
      font: 'Body',
      fontSize: 38,
      localeText: () => i18n('match2.tosContinue.free'),
      onClick: async () => {
        sounds.sfx.play('ok');
        this.closeHandler({ acceptTos: true });
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PUZZLE_PINCH_TOS_NUDGE.CONTINUE,
      },
    });
  }
}
